const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isAutocompleteOption = (value: unknown): value is { value: unknown; label: unknown } =>
  isRecord(value) && 'value' in value && 'label' in value;

const NON_INTERACTIVE_HISTORICAL_FIELD_KEYS = new Set(['templateCode']);

const hasHistoricalValue = (value: unknown): boolean =>
  value instanceof Date ||
  (typeof value === 'string' && Boolean(value.trim())) ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'boolean' && value);

const getChildPath = (path: string, key: string | number) =>
  `${path ? `${path}.` : ''}${key}`;

export const collectHistoricalPrefilledFieldNames = (
  value: unknown,
  excludedTopLevelFields: string[] = [],
  path = ''
): string[] => {
  const excludedFields = new Set(excludedTopLevelFields);

  const collectFieldNames = (currentValue: unknown, currentPath: string): string[] => {
    if (hasHistoricalValue(currentValue)) {
      return currentPath ? [currentPath] : [];
    }

    if (Array.isArray(currentValue)) {
      return currentValue.flatMap((item, index) =>
        collectFieldNames(item, getChildPath(currentPath, index))
      );
    }

    if (!isRecord(currentValue)) {
      return [];
    }

    if (isAutocompleteOption(currentValue)) {
      return currentPath && currentValue.value ? [currentPath] : [];
    }

    return Object.entries(currentValue).flatMap(([key, item]) =>
      (!currentPath && excludedFields.has(key)) || NON_INTERACTIVE_HISTORICAL_FIELD_KEYS.has(key)
        ? []
        : collectFieldNames(item, getChildPath(currentPath, key))
    );
  };

  return collectFieldNames(value, path);
};

export const getHistoricalPrefillFieldName = (
  target: EventTarget | null,
  fieldNames: Iterable<string>
): string | null => {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const fieldElement = target.closest<HTMLElement>('[name], [data-testid]');
  const candidateName =
    fieldElement?.getAttribute('name') ?? fieldElement?.dataset.testid ?? '';

  if (!candidateName) {
    return null;
  }

  return (
    Array.from(fieldNames)
      .sort((firstName, secondName) => secondName.length - firstName.length)
      .find(
        (fieldName) =>
          candidateName === fieldName || candidateName.startsWith(`${fieldName}-`)
      ) ?? null
  );
};
